/**
 * CalendarSelectorImproved - Calendario mejorado con disponibilidad real
 * Integra con Supabase para horarios reales de la clínica
 */

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useBooking } from '../BookingContext';
import { CalendarService, TimeSlot } from '../../../services/calendar-production.service';
import { useToast } from '../../ui/ToastProvider';

interface CalendarSelectorProps {
  onNext: () => void;
  onPrevious: () => void;
  hideNavigation?: boolean;
}

const CalendarSelectorImproved: React.FC<CalendarSelectorProps> = ({ onNext, onPrevious, hideNavigation = false }) => {
  const { state, dispatch } = useBooking();
  const toast = useToast();
  // Semilla desde el contexto para que "Retomar" (UX_VISION P1-5) reabra el calendario
  // en la fecha que el paciente ya había elegido. Sin progreso guardado arranca vacío.
  const [selectedDate, setSelectedDate] = useState<string>(() => state.selectedTimeSlot?.date ?? '');
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [currentMonth, setCurrentMonth] = useState(() =>
    state.selectedTimeSlot?.date ? new Date(state.selectedTimeSlot.date + 'T12:00:00') : new Date()
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isValidatingSlot, setIsValidatingSlot] = useState(false);
  // FUNC-111 — días completos que el terapeuta bloqueó desde /panel ("Bloqueos
  // de agenda"). El backend ya rechaza reservar en ellos; esto los grisa en la
  // grilla para que no se muestren como disponibles al público.
  const [blockedDates, setBlockedDates] = useState<Set<string>>(new Set());
  const timeSlotsPanelRef = useRef<HTMLDivElement>(null);
  // El primer run del efecto de scroll es el montaje. Si `selectedDate` viene
  // sembrado desde el contexto ("Retomar" P1-5, o volver desde el paso de
  // síntesis) NO hay que arrastrar al paciente hacia los horarios: primero ve
  // el calendario y elige el día, y recién ahí baja el scroll — una sola vez.
  const didMountRef = useRef(false);

  // Cargar horarios cuando se selecciona una fecha
  useEffect(() => {
    if (selectedDate && state.selectedTherapist) {
      loadAvailableSlots(selectedDate);
    }
  }, [selectedDate, state.selectedTherapist?.id]);

  // FUNC-111 — cargar los días bloqueados del mes visible (y refetch al navegar
  // de mes o cambiar de terapeuta). Degrada a Set vacío ante error.
  useEffect(() => {
    const therapistId = state.selectedTherapist?.id;
    if (!therapistId) {
      setBlockedDates(new Set());
      return;
    }

    const y = currentMonth.getFullYear();
    const m = currentMonth.getMonth();
    const pad = (n: number) => String(n).padStart(2, '0');
    const start = `${y}-${pad(m + 1)}-01`;
    const end = `${y}-${pad(m + 1)}-${pad(new Date(y, m + 1, 0).getDate())}`;

    let cancelled = false;
    CalendarService.getBlockedDates(therapistId, start, end).then(dates => {
      if (!cancelled) setBlockedDates(dates);
    });
    return () => { cancelled = true; };
  }, [currentMonth, state.selectedTherapist?.id]);

  // Al elegir fecha, el panel de horarios aparece debajo del calendario en mobile
  // (columna única) pero al lado en desktop — 'nearest' no mueve nada si ya está
  // visible y lo trae a la vista si quedó fuera del viewport, sin asumir breakpoint.
  // Doble rAF: llamar scrollIntoView en el mismo tick del click (mismo frame que
  // el whileTap de Framer Motion sobre el day-button) hace que el browser descarte
  // el scroll smooth sin moverse — hay que esperar a que termine ese frame de pintado.
  useEffect(() => {
    // En el montaje no scrolleamos aunque haya fecha sembrada — el paciente
    // tiene que aterrizar viendo el calendario, no los horarios.
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    if (selectedDate) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          timeSlotsPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        });
      });
    }
  }, [selectedDate]);

  // ✅ AUTO-REFRESH: Actualizar slots en background cada 10 segundos (sin interrumpir la UI)
  useEffect(() => {
    if (!selectedDate || !state.selectedTherapist) return;

    const autoRefreshInterval = setInterval(() => {
      console.log('🔄 [AUTO-REFRESH] Actualizando disponibilidad cada 10 segundos');
      loadAvailableSlots(selectedDate, true, true); // silent=true: no spinner, no clear
    }, 10000);

    return () => clearInterval(autoRefreshInterval);
  }, [selectedDate, state.selectedTherapist?.id]);

  // ✅ EVENT LISTENER: Actualizar inmediatamente cuando otros usuarios completen bookings
  useEffect(() => {
    if (!selectedDate || !state.selectedTherapist) return;

    const handleBookingCompleted = (event: any) => {
      const { therapistId, date } = event.detail;
      if (date === selectedDate && therapistId === state.selectedTherapist?.id) {
        loadAvailableSlots(selectedDate, true, true); // silent: no interrumpir selección activa
      }
    };

    window.addEventListener('bookingCompleted', handleBookingCompleted);
    return () => window.removeEventListener('bookingCompleted', handleBookingCompleted);
  }, [selectedDate, state.selectedTherapist?.id]);

  const loadAvailableSlots = async (date: string, forceRefresh: boolean = false, silent: boolean = false) => {
    if (!state.selectedTherapist) return;

    if (!silent) setIsLoading(true);
    try {
      const slots = await CalendarService.getAvailableSlots(
        state.selectedTherapist.id,
        date,
        forceRefresh,
        state.selectedService?.modality,
        state.selectedService?.duration ?? 50
      );
      setTimeSlots(slots);
    } catch (error) {
      console.error('❌ [CALENDAR] Error cargando slots:', error);
      if (!silent) setTimeSlots([]);
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  const handleDateSelect = (date: string) => {
    console.log('📅 [CALENDAR] Fecha seleccionada:', date);
    setSelectedDate(date);
    setTimeSlots([]); // Limpiar slots mientras carga
    
    // Limpiar cache para obtener datos frescos
    if (state.selectedTherapist) {
      CalendarService.clearCacheForDate(state.selectedTherapist.id, date);
    }
  };

  const handleTimeSelect = async (timeSlot: TimeSlot) => {
    if (!timeSlot.available || !state.selectedTherapist || isValidatingSlot) return;

    setIsValidatingSlot(true);
    try {
      const isStillAvailable = await CalendarService.validateSlotAvailability(
        state.selectedTherapist.id,
        selectedDate,
        timeSlot.time,
        state.selectedService?.modality
      );

      if (!isStillAvailable) {
        toast.error('Ese horario acaba de ser ocupado. Elegí otro, por favor.');
        loadAvailableSlots(selectedDate, true);
        return;
      }

      dispatch({
        type: 'SET_TIMESLOT',
        payload: {
          date: selectedDate,
          time: timeSlot.time,
          id: timeSlot.id
        }
      });

      setTimeout(() => {
        onNext();
      }, 800);

    } catch (error) {
      console.error('❌ Error validando disponibilidad:', error);
      toast.error('No pudimos validar la disponibilidad. Intentá nuevamente.');
    } finally {
      setIsValidatingSlot(false);
    }
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startDayOfWeek = (firstDay.getDay() + 6) % 7; // Mon=0, Tue=1, ..., Sun=6
    
    const days = [];
    
    // Días vacíos del mes anterior
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push(null);
    }
    
    // Días del mes actual
    for (let day = 1; day <= daysInMonth; day++) {
      const currentDate = new Date(year, month, day);
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Normalizar para comparación
      currentDate.setHours(0, 0, 0, 0);
      
      const isToday = currentDate.getTime() === today.getTime();
      const isPast = currentDate.getTime() < today.getTime();
      const dayOfWeek = currentDate.getDay();
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
      // Date manual para evitar conversión UTC que puede correrse un día en UTC-3
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      // FUNC-111 — día completo bloqueado por el terapeuta
      const isBlocked = blockedDates.has(dateStr);

      days.push({
        day,
        date: dateStr,
        isToday,
        isPast,
        isWeekend,
        isBlocked,
        // Días no pasados son disponibles (sáb y dom con horario reducido), salvo
        // que el terapeuta haya bloqueado el día completo.
        isAvailable: !isPast && !isBlocked
      });
    }
    
    return days;
  };

  const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  const weekDays = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

  const navigateMonth = (direction: number) => {
    setCurrentMonth(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
  };

  const days = getDaysInMonth(currentMonth);
  const availableSlots = timeSlots.filter(slot => slot.available);
  const hasSelectedTime = state.selectedTimeSlot && state.selectedTimeSlot.date === selectedDate;
  // FUNC-111 — la fecha elegida quedó bloqueada (día completo). Puede pasar si
  // venía sembrada del contexto ("Retomar" P1-5) y el terapeuta la bloqueó
  // después; la grilla ya no deja elegir un día bloqueado nuevo.
  const selectedDateBlocked = !!selectedDate && blockedDates.has(selectedDate);


  return (
    <motion.div 
      className="calendar-selector improved"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
    >
      {/* Header with Progress Context */}
      <motion.div 
        className="step-header with-context"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Progress Context Breadcrumb */}
        <div className="progress-context">
          {state.selectedService && state.selectedTherapist && (
            <div className="context-breadcrumb">
              <span className="context-item">
                <i className="fas fa-check-circle"></i>
                {state.selectedService.name}
              </span>
              <i className="fas fa-chevron-right separator bw-separator"></i>
              <span className="context-item">
                <i className="fas fa-check-circle"></i>
                {state.selectedTherapist.name}
              </span>
              <i className="fas fa-chevron-right separator bw-separator"></i>
              <span className="context-item current">
                <i className="fas fa-calendar-alt"></i>
                Fecha y hora
              </span>
            </div>
          )}
        </div>
      </motion.div>

      <div className="step-content">
        <div className="calendar-container">
          
          {/* Calendario */}
          <motion.div 
            className="calendar-widget"
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <div className="calendar-header bw-calendar-header">
              <button 
                className="btn bw-btn btn-ghost nav-btn bw-nav-btn"
                onClick={() => navigateMonth(-1)}
                disabled={isLoading}
              >
                <i className="fas fa-chevron-left"></i>
              </button>
              
              <h3 className="calendar-title">
                {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
              </h3>
              
              <button 
                className="btn bw-btn btn-ghost nav-btn bw-nav-btn"
                onClick={() => navigateMonth(1)}
                disabled={isLoading}
              >
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>

            <div className="calendar-grid">
              {/* Días de la semana */}
              {weekDays.map(day => (
                <div key={day} className="weekday-header">
                  {day}
                </div>
              ))}

              {/* Días del mes */}
              {days.map((day, index) => (
                <div key={index} className="day-cell">
                  {day && (
                    <motion.button
                      className={`day-button ${
                        day.isPast ? 'past' :
                        !day.isAvailable ? 'unavailable' :
                        day.isToday ? 'today' :
                        selectedDate === day.date ? 'selected' :
                        'available'
                      }`}
                      title={day.isBlocked ? 'El terapeuta no atiende este día' : undefined}
                      onClick={() => day.isAvailable ? handleDateSelect(day.date) : null}
                      disabled={!day.isAvailable || isLoading}
                      whileHover={{ scale: day.isAvailable ? 1.1 : 1 }}
                      whileTap={{ scale: day.isAvailable ? 0.95 : 1 }}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.2, delay: index * 0.01 }}
                    >
                      {day.day}
                      {day.isToday && <div className="today-indicator">Hoy</div>}
                    </motion.button>
                  )}
                </div>
              ))}
            </div>

            <div className="calendar-legend bw-calendar-legend">
              <div className="legend-item bw-legend-item">
                <div className="legend-color bw-legend-color today"></div>
                <span>Hoy</span>
              </div>
              <div className="legend-item bw-legend-item">
                <div className="legend-color bw-legend-color available"></div>
                <span>Disponible</span>
              </div>
              <div className="legend-item bw-legend-item">
                <div className="legend-color bw-legend-color selected"></div>
                <span>Seleccionado</span>
              </div>
              <div className="legend-item bw-legend-item">
                <div className="legend-color bw-legend-color unavailable"></div>
                <span>No disponible</span>
              </div>
            </div>
          </motion.div>

          {/* Panel de horarios */}
          <motion.div
            ref={timeSlotsPanelRef}
            className="time-slots-panel"
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="time-slots-header">
              <h4>
                <i className="fas fa-clock"></i>
                {selectedDate ? (
                  `Horarios para ${new Date(selectedDate + 'T12:00:00-03:00').toLocaleDateString('es-CL', {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric'
                  })}`
                ) : (
                  'Selecciona una fecha'
                )}
              </h4>
              
              {selectedDate && !isLoading && (
                <div className="availability-summary">
                  <span className="available-count">
                    {availableSlots.length} horarios disponibles
                  </span>
                </div>
              )}
            </div>

            <div className="time-slots-content">
              <AnimatePresence>
                {!selectedDate ? (
                  <motion.div 
                    className="no-date-selected"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <i className="fas fa-calendar-plus"></i>
                    <p>Selecciona una fecha en el calendario para ver los horarios disponibles</p>
                  </motion.div>
                ) : isLoading ? (
                  <motion.div 
                    className="loading-slots"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <i className="fas fa-spinner fa-spin"></i>
                    <p>Cargando horarios disponibles...</p>
                  </motion.div>
                ) : selectedDateBlocked ? (
                  <motion.div
                    className="no-slots-available"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                  >
                    <i className="fas fa-calendar-times"></i>
                    <p>El terapeuta no atiende este día</p>
                    <small>Elegí otra fecha en el calendario</small>
                  </motion.div>
                ) : timeSlots.length === 0 || availableSlots.length === 0 ? (
                  <motion.div
                    className="no-slots-available"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                  >
                    <i className="fas fa-calendar-times"></i>
                    <p>No hay horarios disponibles para esta fecha</p>
                    <small>Intenta con otra fecha o contacta con nosotros</small>
                  </motion.div>
                ) : (
                  <motion.div
                    className="time-slots-grid"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    {availableSlots.map((slot, index) => {
                      const isSelected = state.selectedTimeSlot?.id === slot.id;

                      return (
                        <motion.button
                          key={slot.id}
                          className={`time-slot ${isSelected ? 'selected' : 'available'}`}
                          onClick={() => handleTimeSelect(slot)}
                          disabled={isValidatingSlot}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3, delay: index * 0.05 }}
                          whileHover={{ scale: isSelected ? 1 : 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <span className="time-display">
                            {CalendarService.formatTimeForDisplay(slot.time)}
                          </span>

                          {isSelected && (
                            <motion.div
                              className="selected-indicator"
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              transition={{ duration: 0.3 }}
                            >
                              <i className="fas fa-check-circle"></i>
                            </motion.div>
                          )}
                        </motion.button>
                      );
                    })}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Información de horarios */}
            {selectedDate && availableSlots.length > 0 && (
              <motion.div 
                className="time-info"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.3 }}
              >
                <div className="info-grid">
                  <div className="info-item">
                    <i className="fas fa-clock" style={{ color: '#1E4A8F' }}></i>
                    <span>Duración: {state.selectedService?.duration} min</span>
                  </div>
                  <div className="info-item">
                    <i className="fas fa-building" style={{ color: '#3A72C8' }}></i>
                    <span>Bombero Adolfo Ossa 1010, Ofi. 204</span>
                  </div>
                  <div className="info-item">
                    <i className="fas fa-shield-alt" style={{ color: '#057A55' }}></i>
                    <span>Reserva confirmada</span>
                  </div>
                </div>
                
              </motion.div>
            )}
          </motion.div>
        </div>
      </div>

      {/* Acciones — ocultas cuando el componente está embebido en un flujo externo */}
      {!hideNavigation && (
        <motion.div
          className="step-actions"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.4 }}
        >
          <button
            className="btn bw-btn btn-secondary bw-btn-secondary"
            onClick={onPrevious}
            disabled={isLoading}
          >
            <i className="fas fa-arrow-left"></i>
            Anterior
          </button>

          <AnimatePresence>
            {hasSelectedTime && (
              <motion.button
                className="btn bw-btn btn-primary"
                onClick={onNext}
                disabled={isLoading}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.3 }}
              >
                <i className="fas fa-arrow-right"></i>
                Continuar con mis datos
              </motion.button>
            )}
          </AnimatePresence>

          {!hasSelectedTime && (
            <div className="help-text">
              <i className="fas fa-info-circle"></i>
              Selecciona una fecha y hora para continuar
            </div>
          )}
        </motion.div>
      )}
    </motion.div>
  );
};

export default CalendarSelectorImproved;